import React from 'react';
import { getLocalizedPath } from '../utils/navigation';
import { t, getCurrentLanguage } from '../utils/i18n';

// Icon components
const Icon = ({ name, className = "w-5 h-5" }: { name: string; className?: string }) => {
  const icons: { [key: string]: JSX.Element } = {
    facebook: (
      <svg className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    linkedin: (
      <svg className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
  };

  return icons[name] || null;
};

const Footer: React.FC = () => {
  const services = [
    { title: t('footer.services.mortgage_loan'), anchorId: "hipotekarais-kredits" },
    { title: t('footer.services.business_loan'), anchorId: "biznesa-attistibai" },
    { title: t('footer.services.construction_loan'), anchorId: "eku-buvniecibai" },
    { title: t('footer.services.loan_consolidation'), anchorId: "kreditu-apvienosana" },
    { title: t('footer.services.farm_loan'), anchorId: "zemniekiem" },
  ];

  return (
    <footer className="bg-dark-500 text-white py-5 lg:py-10">
      <div className="container max-w-6xl mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-0">
          {/* Logo */}
          <div className="logo">
            <img
              className="w-30 h-10 lg:w-38 lg:h-12 opacity-50"
              src="/finlo_logo.svg"
              alt={t('footer.logo_alt')}
            />
          </div>

          {/* Social Links */}
          <div className="socials flex flex-col items-center">
            <span className="opacity-50 text-xl lg:text-2xl">{t('footer.social_networks')}</span>
            <div className="links flex justify-center gap-2.5 mt-4">
              <a 
                href="https://www.facebook.com/finlo.lv" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon text-2xl hover:text-blue-500 transition-colors"
              >
                <Icon name="facebook" className="w-6 h-6" />
              </a>
              <a 
                href="https://www.linkedin.com/company/finlolv" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon text-2xl hover:text-blue-600 transition-colors"
              >
                <Icon name="linkedin" className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="nav-bar flex flex-col lg:flex-row text-center lg:text-left gap-5 lg:gap-8">
            {/* Services */}
            <div className="services">
              <span className="opacity-50 block mb-2">{t('footer.services_title')}</span>
              <ul className="list-style">
                {services.map((service) => (
                  <li key={service.title} className="service-item">
                    <a 
                      href={`${getLocalizedPath('/loan-types')}#${service.anchorId}`} 
                      className="text-white font-light tracking-wide hover:opacity-50 transition-opacity no-underline"
                    >
                      {service.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Other Links */}
            <div className="other">
              <span className="opacity-50 block mb-2">{t('footer.other_title')}</span>
              <ul className="list-style">
                <li>
                  <a href="#" className="text-white font-light tracking-wide hover:opacity-50 transition-opacity no-underline">
                    {t('footer.about_us')}
                  </a>
                </li>
                <li>
                  <a href={getLocalizedPath('/contact')} className="text-white font-light tracking-wide hover:opacity-50 transition-opacity no-underline">
                    {t('footer.contacts')}
                  </a>
                </li>
                <li>
                  <a href="#faq" className="text-white font-light tracking-wide hover:opacity-50 transition-opacity no-underline">
                    {t('footer.faq')}
                  </a>
                </li>
                <li>
                  <a href={`/documents/${getCurrentLanguage()}/privacy_policy.pdf`} 
                    className="text-white font-light tracking-wide hover:opacity-50 transition-opacity no-underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('footer.privacy_policy')}
                  </a>
                </li>
                <li>
                  <a href={`/documents/${getCurrentLanguage()}/terms.pdf`} 
                    className="text-white font-light tracking-wide hover:opacity-50 transition-opacity no-underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('footer.terms_of_use')}
                  </a>
                </li>
                <li>
                  <a 
                    href="/documents/finlo_di_licence.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white font-light tracking-wide hover:opacity-50 transition-opacity no-underline"
                  >
                    {t('footer.license')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
